/**
 * SchedulesPage - Query Scheduling Management with MDS Components
 */

import React, { useState, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  McDialog,
  McButton,
  McInput,
  McIcon,
  McNotification,
  McSelect,
  McTypeaheadMultiSelect,
  McLoadingIndicator,
  McCard,
  McTag,
} from '@maersk-global/mds-react-wrapper';

// Deep imports for components that might not be in the main index
import { McStepIndicator } from '@maersk-global/mds-react-wrapper/components-core/mc-step-indicator';
import { McStepIndicatorItem } from '@maersk-global/mds-react-wrapper/components-core/mc-step-indicator-item';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';

import { queryService } from '../services/queryService';
import {
  ScheduleQueryResponse,
  ScheduleQueryRequest,
  ScheduleTimeMode,
} from '../types';
import { formatCronExpression, isValidCronExpression } from '../utils/queryUtils';
import { ScheduleCard } from '../components/queries/ScheduleCard';
import { StoredResultsDialog } from '../components/queries/dialogs/StoredResultsDialog';
import { useApplicationAccess } from '../hooks/useApplicationAccess';
import { useAuth } from '../context/AuthContext';
import { roleConfig } from '../config/roles';
import { ConfirmationDialog } from '../components/Common/ConfirmationDialog';
import { TerminalSelect } from '../components/Common/TerminalSelect';

// Constants for Cron Presets
const CRON_PRESETS = [
  { label: 'Every 5 Minutes', value: '0 */5 * * * *' },
  { label: 'Every Hour', value: '0 0 * * * *' },
  { label: 'Every Day (Midnight)', value: '0 0 0 * * *' },
  { label: 'Every Week (Sunday Midnight)', value: '0 0 0 * * 0' },
  { label: 'Every Month (1st Midnight)', value: '0 0 0 1 * *' },
];

const TIMEZONE_OPTIONS = [
  { label: 'UTC', value: 'UTC' },
  { label: 'Local (Browser)', value: Intl.DateTimeFormat().resolvedOptions().timeZone },
];

export default function SchedulesPage() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isResultsDialogOpen, setIsResultsDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<ScheduleQueryResponse | null>(null);
  const [viewingResultsSchedule, setViewingResultsSchedule] = useState<ScheduleQueryResponse | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { canCreate } = useApplicationAccess();
  const { hasRole } = useAuth();
  const isAdmin = hasRole(roleConfig.adminSchedules);
  const hasCreatePermission = canCreate() && isAdmin;
  const [pendingAction, setPendingAction] = useState<{ type: 'save' | 'delete' | 'toggle', payload?: any } | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const [scheduleNameStatus, setScheduleNameStatus] = useState<{ valid: boolean | null, message: string, checking: boolean }>({ valid: null, message: '', checking: false });
  const [tableNameStatus, setTableNameStatus] = useState<{ valid: boolean | null, message: string, checking: boolean }>({ valid: null, message: '', checking: false });

  const initialFormState: ScheduleQueryRequest = {
    queryId: '',
    versionNumber: '',
    terminalIds: [],
    scheduleName: '',
    description: '',
    cronExpression: '0 0 * * * *',
    enabled: true,
    timeMode: 'TERMINAL_LOCAL',
    fixedTimezone: 'UTC',
    resultTableName: '',
  };

  const [formData, setFormData] = useState<ScheduleQueryRequest>(initialFormState);

  // Synchronous ref for all formData to bypass React state stale closures anywhere
  const formDataRef = React.useRef(formData);

  React.useEffect(() => {
    if (location.state?.prefillQueryId && hasCreatePermission) {
      const prefillQueryId = location.state.prefillQueryId;
      const prefillVersion = location.state.prefillVersion || '';
      setEditingSchedule(null);
      const newFormState = { ...initialFormState, queryId: prefillQueryId, versionNumber: prefillVersion };
      setFormData(newFormState);
      formDataRef.current = newFormState;
      setActiveStep(0);
      setApiError(null);
      setScheduleNameStatus({ valid: null, message: '', checking: false });
      setTableNameStatus({ valid: null, message: '', checking: false });
      setIsDialogOpen(true);
      
      // Clear location state to prevent reopening on refresh
      navigate(location.pathname, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, navigate, hasCreatePermission]);

  // Queries
  const { data: schedules, isLoading: schedulesLoading } = useQuery({
    queryKey: ['schedules'],
    queryFn: () => queryService.listSchedules(),
  });

  const { data: queries, isLoading: queriesLoading, refetch: refetchQueries } = useQuery({
    queryKey: ['queries'],
    // Fetch generic list of queries for dropdown
    queryFn: () => queryService.searchQueries({ size: 5000, page: 0 }).then(res => res.content),
  });

  const { data: terminalConfigs, isLoading: terminalsLoading } = useQuery({
    queryKey: ['terminalConfigs'],
    queryFn: () => queryService.listTerminalConfigs(),
  });

  // Versions for selected query (if editing)
  const { data: queryVersions } = useQuery({
    queryKey: ['queryVersions', formData.queryId],
    queryFn: async () => {
      if (!formData.queryId) return [];
      // Assuming we can get full query details to see versions
      // If not readily available, we might need a dedicated endpoint
      // For now, attempting to find it in the list or fetch fresh
      const q = queries?.find(q => q.queryId === formData.queryId);
      if (q && q.versions) return q.versions;
      // Fallback or empty if not loaded
      return [];
    },
    enabled: !!formData.queryId && !!queries
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: ScheduleQueryRequest) => queryService.scheduleQuery(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setApiError(error.response?.data?.detail || error.message || 'Failed to create schedule');
      setPendingAction(null); // Close the confirmation dialog if open
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ScheduleQueryRequest> }) =>
      queryService.updateSchedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
      handleCloseDialog();
    },
    onError: (error: any) => {
      setApiError(error.response?.data?.detail || error.message || 'Failed to update schedule');
      setPendingAction(null); // Close the confirmation dialog if open
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => queryService.deleteSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      queryService.updateSchedule(id, { enabled }),
    onSuccess: () => {
      console.log('Toggle mutation successful, invalidating schedules');
      queryClient.invalidateQueries({ queryKey: ['schedules'] });
    },
    onError: (error: any) => {
      console.error('Toggle mutation failed', error);
      // You might want to show a notification here
    }
  });

  // Freshen dropdowns when dialog opens
  React.useEffect(() => {
    if (isDialogOpen) {
      refetchQueries();
    }
  }, [isDialogOpen, refetchQueries]);

  // Handlers
  const handleOpenCreateDialog = () => {
    setEditingSchedule(null);
    setFormData(initialFormState);
    formDataRef.current = initialFormState;
    setActiveStep(0);
    setApiError(null);
    setScheduleNameStatus({ valid: null, message: '', checking: false });
    setTableNameStatus({ valid: null, message: '', checking: false });
    setIsDialogOpen(true);
  };

  const handleOpenEditDialog = (schedule: ScheduleQueryResponse) => {
    setEditingSchedule(schedule);
    const newFormData = {
      queryId: schedule.queryId,
      versionNumber: schedule.versionNumber,
      terminalIds: schedule.terminalIds,
      scheduleName: schedule.scheduleName,
      description: schedule.description,
      cronExpression: schedule.cronExpression,
      enabled: schedule.enabled,
      timeMode: (schedule.timeMode as ScheduleTimeMode) || 'TERMINAL_LOCAL',
      fixedTimezone: schedule.fixedTimezone || 'UTC',
      resultTableName: schedule.resultTableName,
    };
    setFormData(newFormData);
    formDataRef.current = newFormData;
    setActiveStep(0);
    setApiError(null);
    setScheduleNameStatus({ valid: true, message: '', checking: false });
    setTableNameStatus({ valid: true, message: '', checking: false });
    setIsDialogOpen(true);
  };

  const handleViewResults = (schedule: ScheduleQueryResponse) => {
    setViewingResultsSchedule(schedule);
    setIsResultsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingSchedule(null);
    setFormData(initialFormState);
    formDataRef.current = initialFormState;
    setActiveStep(0);
    setApiError(null);
    setScheduleNameStatus({ valid: null, message: '', checking: false });
    setTableNameStatus({ valid: null, message: '', checking: false });
  };

  const handleInputChange = (field: keyof ScheduleQueryRequest, value: any) => {
    formDataRef.current = { ...formDataRef.current, [field]: value };
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const checkScheduleName = async () => {
    const currentName = formDataRef.current.scheduleName;
    if (!currentName) return;
    setScheduleNameStatus({ valid: null, message: '', checking: true });
    try {
      const res = await queryService.checkScheduleAvailability(currentName, 'SCHEDULE_NAME', editingSchedule?.scheduleId);
      if (formDataRef.current.scheduleName === currentName) {
        setScheduleNameStatus({ valid: res.available, message: res.message || '', checking: false });
      }
    } catch (err) {
      if (formDataRef.current.scheduleName === currentName) {
        setScheduleNameStatus({ valid: false, message: 'Failed to verify', checking: false });
      }
    }
  };

  const checkTableName = async () => {
    const currentName = formDataRef.current.resultTableName;
    if (!currentName) return;
    setTableNameStatus({ valid: null, message: '', checking: true });
    try {
      const res = await queryService.checkScheduleAvailability(currentName, 'RESULT_TABLE_NAME', editingSchedule?.scheduleId);
      if (formDataRef.current.resultTableName === currentName) {
        setTableNameStatus({ valid: res.available, message: res.message || '', checking: false });
      }
    } catch (err) {
      if (formDataRef.current.resultTableName === currentName) {
        setTableNameStatus({ valid: false, message: 'Failed to verify', checking: false });
      }
    }
  };

  const handleSave = () => {
    if (createMutation.isPending || updateMutation.isPending) return;
    setPendingAction({ type: 'save' });
  };

  const handleConfirmAction = React.useCallback(() => {
    if (pendingAction?.type === 'save') {
      if (editingSchedule) {
        updateMutation.mutate({ id: editingSchedule.scheduleId, data: formDataRef.current });
      } else {
        createMutation.mutate(formDataRef.current);
      }
    } else if (pendingAction?.type === 'delete') {
      deleteMutation.mutate(pendingAction.payload);
    } else if (pendingAction?.type === 'toggle') {
      toggleEnabledMutation.mutate(pendingAction.payload);
    }
    setPendingAction(null);
  }, [pendingAction, editingSchedule, updateMutation, createMutation, deleteMutation, toggleEnabledMutation]);

  const filteredSchedules = useMemo(() => {
    if (!schedules) return [];
    return schedules.filter(s =>
      s.scheduleName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.queryId.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [schedules, searchTerm]);

  // Derive versions dropdown data
  const versionOptions = useMemo(() => {
    if (!queryVersions || queryVersions.length === 0) {
      // Fallback if no versions loaded: show current version of query or just allow free text? 
      // Ideally we select from available. If generic `queries` list has `currentVersion`, use that.
      const q = queries?.find(x => x.queryId === formData.queryId);
      if (q) return [{ label: q.currentVersion, value: q.currentVersion }];
      return [];
    }
    return queryVersions.map(v => ({ label: v.versionNumber, value: v.versionNumber }));
  }, [queryVersions, queries, formData.queryId]);

  if (schedulesLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <McLoadingIndicator label="Loading schedules..." />
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: '24px' }}>
        <h1 className="mds-headline--medium-bold" style={{ marginBottom: '8px' }}>
          Schedule Management
        </h1>
        <p className="mds-text--medium-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)', margin: 0 }}>
          Create and manage automated query executions across terminal regions. Set cron expressions and view execution status.
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <McInput
            label="Search schedules"
            hiddenlabel
            placeholder="Search schedules..."
            onInput={(e: any) => setSearchTerm(e.target.value)}
            style={{ width: '300px' }}
          >
            <McIcon icon="magnifying-glass" slot="prefix" />
          </McInput>
          <McTooltip>
            <McButton
              slot="trigger"
              appearance="primary"
              onClick={handleOpenCreateDialog}
              style={{ width: '160px' }}
              disabled={!hasCreatePermission}
            >
              <McIcon icon="plus" slot="prefix" />
              Create Schedule
            </McButton>
            <span slot="content">
              {hasCreatePermission ? "Create a new schedule" : "You do not have permission to create schedules"}
            </span>
          </McTooltip>
        </div>
      </div>

      {filteredSchedules.length === 0 ? (
        <McCard>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            <McIcon icon="calendar" color="#e0e0e0" style={{ marginBottom: '1rem', width: '48px', height: '48px' }} />
            <h3>No Schedules Found</h3>
            <p>Create a new schedule to get started.</p>
          </div>
        </McCard>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))', gap: '1.5rem' }}>
          {filteredSchedules.map(schedule => (
            <ScheduleCard
              key={schedule.scheduleId}
              schedule={schedule}
              onEdit={handleOpenEditDialog}
              onDelete={(id) => setPendingAction({ type: 'delete', payload: id })}
              onToggleEnabled={(id, enabled) => setPendingAction({ type: 'toggle', payload: { id, enabled } })}
              onViewResults={handleViewResults}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Wizard Dialog */}
      <McDialog
        open={isDialogOpen}
        closed={handleCloseDialog}
        heading={editingSchedule ? "Edit Schedule" : "Create New Schedule"}
        width="1000px"
      >
        <div style={{ padding: '1rem', position: 'relative' }}>
          {apiError && (
            <div style={{ marginBottom: '1rem', position: 'sticky', top: 0, zIndex: 50 }}>
              <McNotification appearance="error" heading="Error">
                {apiError}
              </McNotification>
            </div>
          )}
          <McStepIndicator currentindex={activeStep} style={{ marginBottom: '2rem' }}>
            <McStepIndicatorItem state={activeStep > 0 ? "completed" : "current"} label="Information" />
            <McStepIndicatorItem state={activeStep > 1 ? "completed" : (activeStep === 1 ? "current" : "pending")} label="Timing" />
            <McStepIndicatorItem state={activeStep > 2 ? "completed" : (activeStep === 2 ? "current" : "pending")} label="Terminals" />
            <McStepIndicatorItem state={activeStep === 3 ? "current" : "pending"} label="Review" />
          </McStepIndicator>

          <div style={{ minHeight: '300px', marginTop: '1rem' }}>
            {activeStep === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <McInput
                      label="Schedule Name"
                      value={formData.scheduleName}
                      onInput={(e: any) => {
                        handleInputChange('scheduleName', e.target.value);
                        setScheduleNameStatus({ valid: null, message: '', checking: false });
                      }}
                      required
                    />
                    {scheduleNameStatus.message && (
                      <div style={{ color: scheduleNameStatus.valid ? 'green' : 'red', fontSize: '12px', marginTop: '4px' }}>
                        {scheduleNameStatus.message}
                      </div>
                    )}
                  </div>
                  <McButton
                    onClick={checkScheduleName}
                    disabled={!formData.scheduleName || scheduleNameStatus.checking}
                    style={{ marginTop: '24px' }}
                  >
                    {scheduleNameStatus.checking ? 'Checking...' : 'Check'}
                  </McButton>
                </div>
                <McInput
                  label="Description"
                  value={formData.description}
                  onInput={(e: any) => handleInputChange('description', e.target.value)}
                />
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 2 }}>
                    <McSelect
                      label="Query to Schedule"
                      value={formData.queryId}
                      data={(queries || []).map(q => ({ label: q.name || q.queryId, value: q.queryId }))}
                      optionselected={(e: any) => handleInputChange('queryId', e.detail.value)}
                      listsearch={true}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <McSelect
                      label="Query Version"
                      value={formData.versionNumber}
                      data={versionOptions}
                      optionselected={(e: any) => handleInputChange('versionNumber', e.detail.value)}
                      disabled={!formData.queryId}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <McInput
                      label="Result Table Name"
                      value={formData.resultTableName}
                      onInput={(e: any) => {
                        handleInputChange('resultTableName', e.target.value);
                        setTableNameStatus({ valid: null, message: '', checking: false });
                      }}
                      placeholder="e.g. daily_sales_report"
                      required
                    />
                    {tableNameStatus.message && (
                      <div style={{ color: tableNameStatus.valid ? 'green' : 'red', fontSize: '12px', marginTop: '4px' }}>
                        {tableNameStatus.message}
                      </div>
                    )}
                  </div>
                  <McButton
                    onClick={checkTableName}
                    disabled={!formData.resultTableName || tableNameStatus.checking}
                    style={{ marginTop: '24px' }}
                  >
                    {tableNameStatus.checking ? 'Checking...' : 'Check'}
                  </McButton>
                </div>
              </div>
            )}

            {activeStep === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div>
                  <McInput
                    label="Cron Expression (6-field)"
                    value={formData.cronExpression}
                    onInput={(e: any) => handleInputChange('cronExpression', e.target.value)}
                    placeholder="0 0 * * * *"
                  />
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: isValidCronExpression(formData.cronExpression) ? 'var(--mds-color-success-main-background)' : 'var(--mds-color-error-main-background)' }}>
                    {formatCronExpression(formData.cronExpression)}
                  </div>
                </div>

                <McSelect
                  label="Schedule Preset (Optional)"
                  data={CRON_PRESETS}
                  optionselected={(e: any) => handleInputChange('cronExpression', e.detail.value)}
                />

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <McSelect
                    label="Time Mode"
                    value={formData.timeMode}
                    data={[
                      { label: 'Terminal Local Time', value: 'TERMINAL_LOCAL' },
                      { label: 'Fixed Timezone', value: 'FIXED_TIMEZONE' }
                    ]}
                    optionselected={(e: any) => handleInputChange('timeMode', e.detail.value)}
                    style={{ flex: 1 }}
                  />

                  {formData.timeMode === 'FIXED_TIMEZONE' && (
                    <McSelect
                      label="Select Timezone"
                      value={formData.fixedTimezone}
                      data={TIMEZONE_OPTIONS}
                      optionselected={(e: any) => handleInputChange('fixedTimezone', e.detail.value)}
                      style={{ flex: 1 }}
                    />
                  )}
                </div>
              </div>
            )}

            {activeStep === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <p>Select the terminals where this query should be executed:</p>
                <TerminalSelect
                  label="Target Terminals"
                  value={formData.terminalIds}
                  onChange={(selectedValues) => handleInputChange('terminalIds', selectedValues)}
                  isMulti={true}
                />

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '1rem' }}>
                  {formData.terminalIds?.map(tid => (
                    <McTag key={tid} fit="small">{tid}</McTag>
                  ))}
                </div>
              </div>
            )}

            {activeStep === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <h3>Review Schedule Details</h3>
                <McCard>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '8px', fontWeight: 600, width: '150px' }}>Name:</td>
                        <td style={{ padding: '8px' }}>{formData.scheduleName}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', fontWeight: 600 }}>Query:</td>
                        <td style={{ padding: '8px' }}>{formData.queryId} (v{formData.versionNumber})</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', fontWeight: 600 }}>Cron:</td>
                        <td style={{ padding: '8px' }}>{formData.cronExpression}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', fontWeight: 600 }}>Terminals:</td>
                        <td style={{ padding: '8px' }}>{formData.terminalIds?.join(', ')}</td>
                      </tr>
                    </tbody>
                  </table>
                </McCard>
              </div>
            )}
          </div>
        </div>

        <div slot="secondaryAction">
          <McButton variant="plain" onClick={handleCloseDialog} style={{ width: '160px' }}>
            <McIcon icon="times-circle" slot="prefix" />
            Cancel
          </McButton>
        </div>
        <div slot="primaryAction" style={{ display: 'flex', gap: '0.5rem' }}>
          {activeStep > 0 && (
            <McButton variant="plain" onClick={() => setActiveStep(s => s - 1)} style={{ width: '160px' }}>
              <McIcon icon="arrow-left" slot="prefix" />
              Back
            </McButton>
          )}
          {activeStep < 3 ? (
            <McButton
              appearance="primary"
              onClick={() => setActiveStep(s => s + 1)}
              disabled={
                (activeStep === 0 && (!formData.scheduleName || !formData.queryId || !formData.resultTableName || scheduleNameStatus.valid !== true || tableNameStatus.valid !== true)) ||
                (activeStep === 1 && !isValidCronExpression(formData.cronExpression)) ||
                (activeStep === 2 && (!formData.terminalIds || formData.terminalIds.length === 0))
              }
              style={{ width: '160px' }}
            >
              Next
              <McIcon icon="arrow-right" slot="suffix" />
            </McButton>
          ) : (
            <McButton appearance="primary" onClick={handleSave} style={{ width: '160px' }}>
              <McIcon icon="check-circle" slot="prefix" />
              {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
            </McButton>
          )}
        </div>
      </McDialog>

      <StoredResultsDialog
        open={isResultsDialogOpen}
        onClose={() => setIsResultsDialogOpen(false)}
        schedule={viewingResultsSchedule}
      />

      <ConfirmationDialog
        open={!!pendingAction}
        onClose={() => setPendingAction(null)}
        onConfirm={handleConfirmAction}
        title={
          pendingAction?.type === 'delete' ? 'Delete Schedule' :
            pendingAction?.type === 'toggle' ? (pendingAction.payload.enabled ? 'Resume Schedule' : 'Pause Schedule') :
              'Save Schedule'
        }
        content={
          pendingAction?.type === 'delete' ? (
            <p>Are you sure you want to delete this schedule? This action cannot be undone.</p>
          ) : pendingAction?.type === 'toggle' ? (
            <p>Are you sure you want to <strong>{pendingAction.payload.enabled ? 'resume' : 'pause'}</strong> this schedule?</p>
          ) : (
            <div>
              <p>Are you sure you want to {editingSchedule ? 'update' : 'create'} this schedule?</p>
              <ul style={{ marginTop: '8px', listStyleType: 'disc', paddingLeft: '20px' }}>
                <li><strong>Name:</strong> {formData.scheduleName}</li>
                <li><strong>Query:</strong> {formData.queryId}</li>
                <li><strong>Cron:</strong> {formData.cronExpression}</li>
              </ul>
            </div>
          )
        }
        confirmLabel={
          pendingAction?.type === 'delete' ? 'Delete' :
            pendingAction?.type === 'toggle' ? (pendingAction.payload.enabled ? 'Resume' : 'Pause') :
              'Save'
        }
        variant={pendingAction?.type === 'delete' || (pendingAction?.type === 'toggle' && !pendingAction.payload.enabled) ? 'danger' : 'primary'}
        loading={createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || toggleEnabledMutation.isPending}
      />
    </>
  );
}
